import React, { ReactElement } from 'react'
import { Link } from 'react-router-dom'
import { useProductsBySku } from 'lib/modules/catalog'
import { EmptyContainer, Spinner } from 'lib/ui'
import { MagentoProductImage } from 'lib/types/magento'
import { Attribute } from '../FeaturedAttributes'
import { ReactComponent as Flowers } from 'resources/svg/blue-flowers.svg'
import { ItemWrapper, ProductsWrapper, Container, Wrapper } from './styled'

interface Item {
  sku: string | null
  attribute_code: string | null
}

interface Props {
  title: ReactElement
  subTitle?: ReactElement
  items: Item[]
  cta?: ReactElement
}

export const FeaturedProductsSection = ({ title, subTitle, items, cta }: Props) => {
  return (
    <Container>
      <Flowers className='pattern' />
      <Wrapper>
        {title && (
          <>{title}</>
        )}
        {subTitle && (
          <>{subTitle}</>
        )}
        <ProductList items={items} />
        {cta && (
          <>{cta}</>
        )}
      </Wrapper>
      <Flowers className='pattern pattern-right' />
    </Container>
  )
}

const ProductList = ({ items }: { items: Item[] }) => {
  const skus = items.reduce((acc: string[], item: Item, i: number) => {
    if (item.sku) {
      acc.push(item.sku)
    }
    return acc
  }, [])
  const { products, isLoading } = useProductsBySku({ sku: skus })

  if (isLoading) {
    return (
      <EmptyContainer>
        <Spinner />
      </EmptyContainer>
    )
  }

  return (
    <ProductsWrapper>
      {products?.map(product => {
        const attribute = items.find(item => item.sku === product.sku)?.attribute_code
        return (
          <ProductCard key={product.id} {...product} attributeToDisplay={attribute ?? ''} />
        )
      })}
    </ProductsWrapper>
  )
}

interface Product {
  customAttribute?: Record<string, string[]>
  getUrlPath: () => string
  image: {
    small: MagentoProductImage
  }
  name: string
  attributeToDisplay: string
}

const ProductCard = ({ customAttribute, getUrlPath, image, attributeToDisplay, name }: Product) => {
  const [attribute] = customAttribute?.[attributeToDisplay] ?? []

  return (
    <ItemWrapper>
      <div className='product-attribute'>
        <Attribute attribute={attributeToDisplay} value={attribute} />
      </div>
      <div className='product-info'>
        <div className='product-image' style={{ backgroundImage: `url(${image.small.url})` }} title={name} />
        <Link to={getUrlPath()}>View Product</Link>
      </div>
    </ItemWrapper>
  )
}
